import { APP_CONFIG } from "../config/appConfig";
import type { AuthUser } from "./auth";

/**
 * 100% Centralized & Configurable Analytics Event Dictionary.
 * All event names across the entire app must reference this registry.
 */
export const ANALYTICS_EVENTS = {
  // Subscription & Monetization Flow
  SUBSCRIPTION_VIEW_PROMPT: "view_subscription_prompt",
  SUBSCRIPTION_CLICK_GO_PREMIUM: "click_go_premium",
  SUBSCRIPTION_VIEW_PACKAGES: "view_item_list",
  SUBSCRIPTION_SELECT_PACKAGE: "select_item",
  SUBSCRIPTION_SELECT_PAYMENT_METHOD: "select_payment_method",
  SUBSCRIPTION_BEGIN_CHECKOUT: "begin_checkout",
  SUBSCRIPTION_REQUEST_OTP: "request_payment_otp",
  SUBSCRIPTION_GENERATE_QR: "generate_payment_qr",
  SUBSCRIPTION_SUCCESS: "purchase",
  SUBSCRIPTION_FAILED: "subscription_failed",
  SUBSCRIPTION_BEGIN_UNSUBSCRIBE: "begin_unsubscribe",
  SUBSCRIPTION_UNSUBSCRIBE_SUCCESS: "unsubscribe_success",
  PREMIUM_PROMOTION_VIEW: "view_promotion",
  PREMIUM_PROMOTION_SELECT: "select_promotion",
  PREMIUM_PROMOTION_SKIP: "skip_promotion",

  // One-off Purchases (CRBT & Audio Library)
  PURCHASE_CRBT_TONE: "purchase_crbt_tone",
  PURCHASE_AUDIO_LIBRARY: "purchase_audio_library",

  // App Lifecycle, Auth & Navigation
  PAGE_VIEW: "page_view",
  AUTH_LOGIN: "login",
  AUTH_SIGN_UP: "sign_up",
  AUTH_LOGOUT: "logout",
  PLAY_TRACK: "play_track",
  SEARCH_QUERY: "search",
} as const;

export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

/**
 * Centralized Parameter Keys
 */
export const ANALYTICS_PARAMS = {
  TRIGGER_SOURCE: "trigger_source",
  ENTRY_POINT: "entry_point",
  ITEM_ID: "item_id",
  ITEM_NAME: "item_name",
  PRICE: "price",
  VALUE: "value",
  CURRENCY: "currency",
  PAYMENT_METHOD: "payment_method",
  PRODUCT_CODE: "product_code",
  TRANSACTION_ID: "transaction_id",
  FLOW_TYPE: "flow_type",
  ERROR_TYPE: "error_type",
  ERROR_MESSAGE: "error_message",
  ACTION_TYPE: "action_type",
  TRACK_ID: "track_id",
  TRACK_TITLE: "track_title",
  ARTIST_NAME: "artist_name",
  SEARCH_TERM: "search_term",
  CATEGORY_FILTER: "category_filter",
  METHOD: "method",
  CAMPAIGN_ID: "campaign_id",
  CREATIVE_NAME: "creative_name",
} as const;

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

const isDev = Boolean(import.meta.env.DEV);

let isInitialized = false;
let currentMeasurementId: string | null = null;

export function _resetAnalyticsForTesting(): void {
  isInitialized = false;
  currentMeasurementId = null;
}

/**
 * Initialize Google Analytics 4 dynamically.
 * Safe to call multiple times (idempotent).
 */
export function initGA(customId?: string): void {
  if (typeof window === "undefined") return;

  // Prevent initializing inside the development mobile simulator iframe
  try {
    if (window.self !== window.top && import.meta.env.DEV) {
      return;
    }
  } catch {}

  const measurementId = customId !== undefined ? customId : APP_CONFIG.gaMeasurementId;
  if (!measurementId || measurementId === "G-XXXXXXXXXX") {
    // Graceful no-op when ID is not set or placeholder
    return;
  }

  if (isInitialized && currentMeasurementId === measurementId) {
    return;
  }

  currentMeasurementId = measurementId;

  // Initialize dataLayer and gtag stub if not present
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer?.push(arguments);
    };
  }

  window.gtag("js", new Date());
  // Standard GA4 Enhanced Measurement handles page views and SPA browser history transitions natively
  // Enable debug_mode: true in development for GA4 DebugView in Google Analytics dashboard
  window.gtag("config", measurementId, {
    debug_mode: isDev,
  });

  // Dynamically inject script tag if not already on the page
  const existingScript = document.getElementById("ga4-gtag-script");
  if (!existingScript) {
    const script = document.createElement("script");
    script.id = "ga4-gtag-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }

  isInitialized = true;
  if (isDev) {
    console.debug(`[GA4 Init] Initialized with measurementId: ${measurementId}, debug_mode: true`);
  }
}

/**
 * Dispatches a standard custom or standard GA4 event.
 */
export function trackEvent(
  eventName: AnalyticsEventName | string,
  params: Record<string, any> = {}
): void {
  if (typeof window === "undefined") return;

  const eventParams = isDev ? { ...params, debug_mode: true } : params;

  if (isDev) {
    console.debug(`[GA4 Event: ${eventName}]`, eventParams);
  }

  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, eventParams);
  }
}

/**
 * Track SPA Page View on route change.
 */
export function trackPageView(path: string, title?: string): void {
  if (typeof window === "undefined") return;

  trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
    page_path: path,
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
    page_location: typeof window !== "undefined" ? window.location.href : "",
  });
}

/**
 * Set user identity and persistent user properties in GA4.
 */
export function setAnalyticsUser(user: AuthUser | null): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  if (!user) {
    clearAnalyticsUser();
    return;
  }

  const isPremium = Boolean(
    user.is_premium ||
    user.role === "premium" ||
    user.role === "vip"
  );

  window.gtag("set", "user_properties", {
    user_id: user.id ? String(user.id) : undefined,
    user_role: user.role || (user.is_guest ? "guest" : "user"),
    is_premium: isPremium,
    is_guest: Boolean(user.is_guest),
  });

  if (user.id) {
    window.gtag("config", currentMeasurementId || APP_CONFIG.gaMeasurementId || "", {
      user_id: String(user.id),
    });
  }
}

/**
 * Clear user identity on logout.
 */
export function clearAnalyticsUser(): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  window.gtag("set", "user_properties", {
    user_id: null,
    user_role: "guest",
    is_premium: false,
    is_guest: true,
  });
}

/* =========================================================================
   STRONG TYPED SUBSCRIPTION & MONETIZATION HELPERS
   ========================================================================= */

/**
 * Track subscription prompt modal open.
 */
export function trackSubscriptionPrompt(triggerSource: string = "unknown"): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_VIEW_PROMPT, {
    [ANALYTICS_PARAMS.TRIGGER_SOURCE]: triggerSource,
  });
}

/**
 * Track clicks on "Go Premium" or "✨ Premium" buttons.
 */
export function trackClickGoPremium(entryPoint: string = "unknown"): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CLICK_GO_PREMIUM, {
    [ANALYTICS_PARAMS.ENTRY_POINT]: entryPoint,
  });
}

export function trackPremiumPromotionView(campaignId: string, productCode: string): void {
  trackEvent(ANALYTICS_EVENTS.PREMIUM_PROMOTION_VIEW, {
    [ANALYTICS_PARAMS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PARAMS.CREATIVE_NAME]: "premium_limited_time_offer",
    [ANALYTICS_PARAMS.PRODUCT_CODE]: productCode,
  });
}

export function trackPremiumPromotionSelect(campaignId: string, productCode: string): void {
  trackEvent(ANALYTICS_EVENTS.PREMIUM_PROMOTION_SELECT, {
    [ANALYTICS_PARAMS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PARAMS.CREATIVE_NAME]: "premium_limited_time_offer",
    [ANALYTICS_PARAMS.PRODUCT_CODE]: productCode,
  });
}

export function trackPremiumPromotionSkip(campaignId: string, productCode: string): void {
  trackEvent(ANALYTICS_EVENTS.PREMIUM_PROMOTION_SKIP, {
    [ANALYTICS_PARAMS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PARAMS.CREATIVE_NAME]: "premium_limited_time_offer",
    [ANALYTICS_PARAMS.PRODUCT_CODE]: productCode,
  });
}

/**
 * Track viewing available subscription packages list.
 */
export function trackViewPackages(
  packages: Array<{ id: number | string; name: string; price: number | string; product_code?: string }>
): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_VIEW_PACKAGES, {
    item_list_name: "Subscription Packages",
    items: packages.map((pkg, idx) => ({
      item_id: String(pkg.id),
      item_name: pkg.name,
      index: idx,
      price: Number(pkg.price) || 0,
      currency: "MMK",
      item_category: "Subscription",
      item_variant: pkg.product_code || "",
    })),
  });
}

/**
 * Track selecting a specific package.
 */
export function trackSelectPackage(
  pkg: { id: number | string; name: string; price: number | string; product_code?: string },
  paymentMethod?: string
): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_SELECT_PACKAGE, {
    [ANALYTICS_PARAMS.ITEM_ID]: String(pkg.id),
    [ANALYTICS_PARAMS.ITEM_NAME]: pkg.name,
    [ANALYTICS_PARAMS.PRICE]: Number(pkg.price) || 0,
    [ANALYTICS_PARAMS.CURRENCY]: "MMK",
    [ANALYTICS_PARAMS.PRODUCT_CODE]: pkg.product_code || "",
    ...(paymentMethod ? { [ANALYTICS_PARAMS.PAYMENT_METHOD]: paymentMethod } : {}),
  });
}

/**
 * Track selecting a payment method.
 */
export function trackSelectPaymentMethod(paymentMethod: string, packageId?: number | string | null): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_SELECT_PAYMENT_METHOD, {
    [ANALYTICS_PARAMS.PAYMENT_METHOD]: paymentMethod,
    ...(packageId ? { [ANALYTICS_PARAMS.ITEM_ID]: String(packageId) } : {}),
  });
}

/**
 * Track initiating the checkout flow (clicking Next on package & payment method).
 */
export function trackBeginCheckout(
  pkg: { id: number | string; name: string; price: number | string; product_code?: string },
  paymentMethod: string
): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_BEGIN_CHECKOUT, {
    [ANALYTICS_PARAMS.ITEM_ID]: String(pkg.id),
    [ANALYTICS_PARAMS.ITEM_NAME]: pkg.name,
    [ANALYTICS_PARAMS.PRICE]: Number(pkg.price) || 0,
    [ANALYTICS_PARAMS.VALUE]: Number(pkg.price) || 0,
    [ANALYTICS_PARAMS.CURRENCY]: "MMK",
    [ANALYTICS_PARAMS.PAYMENT_METHOD]: paymentMethod,
    [ANALYTICS_PARAMS.PRODUCT_CODE]: pkg.product_code || "",
    items: [
      {
        item_id: String(pkg.id),
        item_name: pkg.name,
        price: Number(pkg.price) || 0,
        currency: "MMK",
        item_variant: pkg.product_code || "",
      },
    ],
  });
}

/**
 * Track OTP request for ATOM carrier billing.
 */
export function trackSubscriptionRequestOtp(
  productCode: string,
  flowType: "subscription" | "audio_library" | "unsubscribe" = "subscription"
): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_REQUEST_OTP, {
    [ANALYTICS_PARAMS.PRODUCT_CODE]: productCode,
    [ANALYTICS_PARAMS.FLOW_TYPE]: flowType,
  });
}

/**
 * Track QR generation for AYA Pay.
 */
export function trackSubscriptionGenerateQr(productCode: string, amount: string | number): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_GENERATE_QR, {
    [ANALYTICS_PARAMS.PRODUCT_CODE]: productCode,
    [ANALYTICS_PARAMS.PRICE]: Number(amount) || 0,
    [ANALYTICS_PARAMS.CURRENCY]: "MMK",
  });
}

/**
 * Track successful subscription activation / purchase.
 */
export function trackSubscriptionSuccess(details: {
  transactionId?: string | number;
  value?: number | string;
  itemId?: number | string;
  itemName?: string;
  paymentMethod?: string;
  productCode?: string;
}): void {
  const numericVal = Number(details.value) || 0;
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_SUCCESS, {
    [ANALYTICS_PARAMS.TRANSACTION_ID]: details.transactionId ? String(details.transactionId) : undefined,
    [ANALYTICS_PARAMS.VALUE]: numericVal,
    [ANALYTICS_PARAMS.CURRENCY]: "MMK",
    [ANALYTICS_PARAMS.ITEM_ID]: details.itemId ? String(details.itemId) : undefined,
    [ANALYTICS_PARAMS.ITEM_NAME]: details.itemName || "Yours Music Premium",
    [ANALYTICS_PARAMS.PAYMENT_METHOD]: details.paymentMethod || "ATOM",
    [ANALYTICS_PARAMS.PRODUCT_CODE]: details.productCode || "",
    items: [
      {
        item_id: details.itemId ? String(details.itemId) : details.productCode || "premium",
        item_name: details.itemName || "Yours Music Premium",
        price: numericVal,
        currency: "MMK",
      },
    ],
  });
}

/**
 * Track subscription or payment failure.
 */
export function trackSubscriptionFailed(details: {
  itemId?: number | string;
  paymentMethod?: string;
  errorType?: string;
  errorMessage?: string;
  productCode?: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_FAILED, {
    [ANALYTICS_PARAMS.ITEM_ID]: details.itemId ? String(details.itemId) : undefined,
    [ANALYTICS_PARAMS.PAYMENT_METHOD]: details.paymentMethod,
    [ANALYTICS_PARAMS.ERROR_TYPE]: details.errorType || "unknown_error",
    [ANALYTICS_PARAMS.ERROR_MESSAGE]: details.errorMessage || "",
    [ANALYTICS_PARAMS.PRODUCT_CODE]: details.productCode || "",
  });
}

/**
 * Track initiation of unsubscription flow.
 */
export function trackBeginUnsubscribe(productCode: string, subscriptionName?: string): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_BEGIN_UNSUBSCRIBE, {
    [ANALYTICS_PARAMS.PRODUCT_CODE]: productCode,
    [ANALYTICS_PARAMS.ITEM_NAME]: subscriptionName || "",
  });
}

/**
 * Track successful unsubscription.
 */
export function trackUnsubscribeSuccess(productCode: string, actionType?: string): void {
  trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_UNSUBSCRIBE_SUCCESS, {
    [ANALYTICS_PARAMS.PRODUCT_CODE]: productCode,
    [ANALYTICS_PARAMS.ACTION_TYPE]: actionType || "delete-subscription",
  });
}

/**
 * Track CRBT tone purchase.
 */
export function trackCrbtPurchase(details: {
  toneId: string | number;
  toneName: string;
  artistName?: string;
  price?: number | string;
}): void {
  trackEvent(ANALYTICS_EVENTS.PURCHASE_CRBT_TONE, {
    [ANALYTICS_PARAMS.ITEM_ID]: String(details.toneId),
    [ANALYTICS_PARAMS.ITEM_NAME]: details.toneName,
    [ANALYTICS_PARAMS.ARTIST_NAME]: details.artistName || "",
    [ANALYTICS_PARAMS.PRICE]: Number(details.price) || 0,
    [ANALYTICS_PARAMS.CURRENCY]: "MMK",
  });
}

/**
 * Track Audio Library purchase.
 */
export function trackAudioLibraryPurchase(details: {
  audioId: number | string;
  price?: number | string;
  status: "success" | "failed";
  errorMessage?: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.PURCHASE_AUDIO_LIBRARY, {
    [ANALYTICS_PARAMS.ITEM_ID]: String(details.audioId),
    [ANALYTICS_PARAMS.PRICE]: Number(details.price) || 0,
    [ANALYTICS_PARAMS.CURRENCY]: "MMK",
    status: details.status,
    ...(details.errorMessage ? { [ANALYTICS_PARAMS.ERROR_MESSAGE]: details.errorMessage } : {}),
  });
}

/* =========================================================================
   GENERAL APP ENGAGEMENT HELPERS
   ========================================================================= */

export function trackLogin(method: string): void {
  trackEvent(ANALYTICS_EVENTS.AUTH_LOGIN, { [ANALYTICS_PARAMS.METHOD]: method });
}

export function trackSignUp(method: string): void {
  trackEvent(ANALYTICS_EVENTS.AUTH_SIGN_UP, { [ANALYTICS_PARAMS.METHOD]: method });
}

export function trackLogout(): void {
  trackEvent(ANALYTICS_EVENTS.AUTH_LOGOUT);
}

export function trackPlayTrack(details: {
  trackId: number | string;
  title: string;
  artist?: string;
  source?: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.PLAY_TRACK, {
    [ANALYTICS_PARAMS.TRACK_ID]: String(details.trackId),
    [ANALYTICS_PARAMS.TRACK_TITLE]: details.title,
    [ANALYTICS_PARAMS.ARTIST_NAME]: details.artist || "",
    [ANALYTICS_PARAMS.TRIGGER_SOURCE]: details.source || "unknown",
  });
}

export function trackSearch(query: string, category?: string): void {
  if (!query || !query.trim()) return;
  trackEvent(ANALYTICS_EVENTS.SEARCH_QUERY, {
    [ANALYTICS_PARAMS.SEARCH_TERM]: query.trim(),
    ...(category ? { [ANALYTICS_PARAMS.CATEGORY_FILTER]: category } : {}),
  });
}
